<template>
  <div
    class="coupon-detail"
    :class="{
      'coupon-detail--disabled': disabled
    }"
    ref="couponDetailRef"
    v-if="coupon"
    v-show="!isScrolling"
  >
    <div
      class="coupon-detail-preview"
      ref="couponDetailPreviewRef"
      v-if="!show"
      @click="onPreviewClick"
    >
      <img
        class="coupon-detail-preview__gift"
        src="https://cdn-image.bestdealer.com/app-bestdealer/1688/coupon/gift.png"
      >
      <div class="coupon-detail-preview__content">
        <div class="coupon-detail-preview__title">
          <IntlCurrency
            class="coupon-detail-preview__currency"
            currency="USD"
          />
          <span class="coupon-detail-preview__amount">{{ coupon.discountAmount }}</span>
          Off
        </div>
        <span class="coupon-detail-preview__text">Over US ${{ coupon.minOrderAmount }}</span>
      </div>
      <Countdown
        class="coupon-detail-preview__time"
        :time="coupon.endTimeUser"
        @end="reset"
      />
      <Icon
        class="coupon-detail-preview__arrow"
        name="arrow-right-l-2"
      />
    </div>
    <div
      class="coupon-detail__content"
      ref="couponDetailContentRef"
      v-else
    >
      <p class="coupon-detail__title">
        Spend & Save Coupon
      </p>
      <Icon
        class="coupon-detail__close"
        name="close-l"
        @click="show = false"
      />
      <div class="coupon-detail-coupon">
        <div class="coupon-detail-coupon__left">
          <div class="coupon-detail__off">
            <IntlCurrency
              class="coupon-detail__currency"
              currency="USD"
            />
            <span class="coupon-detail__amount">{{ coupon.discountAmount }}</span>
            Off
          </div>
          <p class="coupon-detail__total">
            Total items over US ${{ coupon.minOrderAmount }}
          </p>
          <p class="coupon-detail__extra">
            {{ coupon.displayText }}
          </p>
        </div>
        <div class="coupon-detail-coupon__right">
          <p class="coupon-detail__available">
            Available
          </p>
          <Countdown
            class="coupon-detail__valid"
            :time="coupon.endTimeUser"
            @end="reset"
          />
        </div>
      </div>
      <div
        class="coupon-detail-delete__wrapper"
        @click="invalidateCoupon(coupon.couponCode)"
      >
        <p class="coupon-detail-delete">
          <Icon
            class="coupon-detail-delete__icon"
            name="delete-trash"
          />
          <span class="coupon-detail-delete__text">Delete</span>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { IntlCurrency } from '@/components/NewIntlCurrency'
import { useCoupon } from '@/views/Carts/hooks/useCoupon'
import { Countdown } from '@/components/Countdown'
import { Icon } from '@/components/Icon'

const { coupon, invalidateCoupon, reset } = useCoupon()

const show = ref(false)
const couponDetailRef = ref(null)
const couponDetailPreviewRef = ref(null)
const couponDetailContentRef = ref(null)

defineProps({
  disabled: {
    type: Boolean,
    default: false
  }
})

watch(show, () => {
  const el = couponDetailRef.value
  if (!el) return
  const contentHeight = (show.value ? couponDetailPreviewRef.value : couponDetailContentRef.value)?.offsetHeight
  if (contentHeight) el.style.height = `${contentHeight}px`

  nextTick(() => {
    const contentHeight = (show.value ? couponDetailContentRef.value : couponDetailPreviewRef.value)?.offsetHeight
    if (contentHeight) el.style.height = `${contentHeight}px`
  })
})

const onPreviewClick = () => {
  show.value = true
}

/** 判断容器是否处于滚动态，若容器静止则显示，容器滚动则隐藏 */
const isScrolling = ref(false)

let scrollEl

let timer

const onScroll = () => {
  if (timer) clearTimeout(timer)
  isScrolling.value = true

  timer = setTimeout(() => {
    isScrolling.value = false
  }, 2000)
}

onMounted(() => {
  scrollEl = document.querySelector('.h5-carts__content')

  if (scrollEl == null) return

  scrollEl.addEventListener('scroll', onScroll)
})

onBeforeUnmount(() => {
  scrollEl.removeEventListener('scroll', onScroll)
})
</script>

<style lang="less" scoped>
.coupon-detail {
  --opacity: 1;

  position: relative;
  border-radius: 8px;
  background-color: #F0F9FD;
  font-family: mon-600;
  font-size: 12px;
  color: #052784;
  transition: height .3s ease-in-out;
  box-shadow: 0px -4px 8px 0px #1414140A;

  &:has(.coupon-detail__content) {
    overflow: hidden;
  }

  &--disabled {
    --opacity: .5;

    background: #EBEBEB;

    .coupon-detail-preview {
      filter: grayscale(1);
      opacity: 0.5;
    }

    .coupon-detail__content {
      filter: grayscale(1);
    }
  }

  &-preview {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    cursor: pointer;

    &__gift {
      margin-right: 8px;
      width: 24px;
      height: 24px;
    }

    &__content {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    &__title {
      display: flex;
      align-items: center;
      font-size: 12px;
    }

    &__amount {
      font-family: mon-700;
      padding-left: 2px;
      padding-right: 2px;
      font-size: 14px;
      line-height: 18px;
      color: #F55745;
    }

    &__text {
      font-size: 11px;
      line-height: 14px;
      background: linear-gradient(90deg, #052784 0%, #E14D39 100%);
      background-clip: text;
      -webkit-background-clip: text;
      color: transparent;
    }

    &__time {
      margin-left: 16px;
    }

    &__arrow {
      margin-left: auto;
      font-size: 14px;
    }
  }

  &__content {
    padding: 16px 12px 12px;
  }

  &__title {
    font-family: mon-700;
    font-size: 14px;
    line-height: 18px;
    opacity: var(--opacity);
  }

  &__close {
    position: absolute;
    top: 15px;
    right: 12px;
    font-size: 14px;
    opacity: var(--opacity);
  }

  &-coupon {
    /** Coupon style */
    mask-image: radial-gradient(circle at 58% 8px, transparent 8px, red 8.5px), linear-gradient(transparent 25%, red 0px, red 75%, transparent 0px);
    mask-size: 100%, 1px 8px;
    mask-repeat: repeat, repeat-y;
    mask-position: 0px -8px, 58% center;
    mask-composite: subtract;
    background-color: #FFFFFF;
    border-radius: 6px;
    opacity: var(--opacity);

    margin-top: 12px;
    position: relative;
    display: flex;
    height: 88px;

    &__left, &__right {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      text-align: center;
      padding: 0 4px;
    }

    &__left {
      width: 58%;
    }

    &__right {
      width: 42%;
    }
  }

  &__off {
    display: flex;
    align-items: center;
    font-family: mon-600;
    font-size: 14px;
    line-height: 18px;
  }

  &__amount {
    padding: 0 4px;
    font-family: mon-700;
    font-size: 36px;
    line-height: 40px;
    color: #F55745;
  }

  &__total {
    font-family: mon-600;
    font-size: 12px;
    line-height: 16px;
  }

  &__extra {
    font-family: mon-600;
    font-size: 10px;
    line-height: 14px;
  }

  &__available {
    padding: 5px 15px;
    border-radius: 20px;
    border: 1px solid #EBF1F5;
    font-family: mon-500;
    font-size: 12px;
    line-height: 16px;
    color: #232323;
  }

  &__valid {
    margin-top: 8px;
  }

  &-delete {
    margin-top: 12px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;

    &__wrapper {
      text-align: center;
    }

    &__icon {
      font-size: 16px;
      color: #232323;
    }

    &__text {
      font-family: mon-500;
      font-size: 12px;
      line-height: 16px;
      text-decoration: underline;
      color: #232323;
    }
  }
}
</style>
